import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFeatures } from '../context/FeatureFlagContext';
import {
  ALL_MODELS,
  LEGACY_MODELS,
  MODEL_ALIASES,
  NEW_AGENT_DEFAULT_MODEL,
  NEW_AGENT_MODELS,
  REASONING_LEVELS,
} from './taskModelOptions';

interface TaskModelSettingsProps {
  assignedToAI: boolean;
  modelName?: string;
  reasoningLevel?: string;
  workstreamID: string;
  taskID: string;
  version: number;
  onSaved?: (updated: { ModelName: string; ReasoningLevel: string; Version: number }) => void;
}

function normalizeModelName(modelName: string | undefined, fallback: string): string {
  if (!modelName) return fallback;
  const normalized = modelName === 'Claude 4.6 Opus (1M)' ? 'Claude 4.6 Opus' : modelName;
  if (ALL_MODELS.includes(normalized)) return normalized;
  const migrated = MODEL_ALIASES[normalized];
  return migrated && ALL_MODELS.includes(migrated) ? migrated : fallback;
}

function normalizeReasoningLevel(reasoningLevel: string | undefined): string {
  return REASONING_LEVELS.some((level) => level.value === reasoningLevel) ? reasoningLevel as string : 'Medium';
}

export default function TaskModelSettings({
  assignedToAI,
  modelName,
  reasoningLevel,
  workstreamID,
  taskID,
  version,
  onSaved,
}: TaskModelSettingsProps) {
  if (!assignedToAI) {
    return null;
  }

  const features = useFeatures();
  const visibleModels = features['NewAgent'] ? NEW_AGENT_MODELS : LEGACY_MODELS;
  const defaultModel = features['NewAgent'] ? NEW_AGENT_DEFAULT_MODEL : LEGACY_MODELS[0];
  const normalizedModel = useMemo(
    () => normalizeModelName(modelName, defaultModel),
    [modelName, defaultModel],
  );
  const normalizedReasoning = useMemo(
    () => normalizeReasoningLevel(reasoningLevel),
    [reasoningLevel],
  );
  const [selectedModel, setSelectedModel] = useState(normalizedModel);
  const [selectedReasoning, setSelectedReasoning] = useState(normalizedReasoning);
  const [savingField, setSavingField] = useState<'model' | 'reasoning' | null>(null);

  useEffect(() => {
    setSelectedModel(normalizedModel);
  }, [normalizedModel]);

  useEffect(() => {
    setSelectedReasoning(normalizedReasoning);
  }, [normalizedReasoning]);

  const updateTask = useCallback(async (fields: Record<string, string>, rollback: () => void) => {
    setSavingField(Object.prototype.hasOwnProperty.call(fields, 'ModelName') ? 'model' : 'reasoning');
    try {
      const resp = await fetch(
        `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(taskID)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Version: version, Fields: fields }),
        },
      );
      if (!resp.ok) {
        rollback();
        return;
      }
      const data = await resp.json();
      onSaved?.({
        ModelName: normalizeModelName(data.ModelName, defaultModel),
        ReasoningLevel: normalizeReasoningLevel(data.ReasoningLevel),
        Version: data.Version,
      });
    } catch {
      rollback();
    } finally {
      setSavingField(null);
    }
  }, [workstreamID, taskID, version, onSaved, defaultModel]);

  const disabled = savingField !== null;

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: 500, color: '#24292f' }}>Model</span>
          <select
            value={selectedModel}
            disabled={disabled}
            onChange={(e) => {
              const nextValue = e.target.value;
              const previousValue = selectedModel;
              setSelectedModel(nextValue);
              void updateTask({ ModelName: nextValue }, () => setSelectedModel(previousValue));
            }}
            style={{ padding: '6px 8px', minWidth: '220px' }}
          >
            {visibleModels.map((model) => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: 500, color: '#24292f' }}>Reasoning</span>
          <select
            value={selectedReasoning}
            disabled={disabled}
            onChange={(e) => {
              const nextValue = e.target.value;
              const previousValue = selectedReasoning;
              setSelectedReasoning(nextValue);
              void updateTask({ ReasoningLevel: nextValue }, () => setSelectedReasoning(previousValue));
            }}
            style={{ padding: '6px 8px', minWidth: '140px' }}
          >
            {REASONING_LEVELS.map((level) => (
              <option key={level.value} value={level.value}>{level.label}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
